import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { IsRestoringProvider } from '@tanstack/react-query';
import {
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from '@tanstack/react-query-persist-client';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { useAuthContext } from '../context/AuthProvider';
import {
  AccessOracleProvider,
  createAccessOracle,
  type AccessOracle,
} from '../lib/access/accessOracle';
import { setAccessOracle } from '../lib/access/accessOracleSingleton';
import {
  PERSIST_BUSTER,
  PERSIST_MAX_AGE,
  PERSIST_RESTORE_TIMEOUT_MS,
} from '../lib/pwa/queryConfig';
import {
  __resetOfflineDebugState,
  updateOfflineDebugState,
} from '../lib/pwa/offlineDebug';
import { shouldPersistQuery } from '../lib/pwa/queryPersistence';
import { createUserQueryPersister } from '../lib/pwa/queryPersister';
import { startAutoSync } from '../lib/pwa/syncManager';
import { purgeLegacyStores, setCurrentUserId } from '../lib/pwa/userScope';

// --- Store externo para isRestoring (evita setState síncrono no efeito) ---

let _isRestoring = true;
const _restoringListeners = new Set<() => void>();

function setRestoring(value: boolean) {
  if (_isRestoring === value) return;
  _isRestoring = value;
  updateOfflineDebugState({ isRestoring: value });
  _restoringListeners.forEach((cb) => cb());
}

function subscribeRestoring(cb: () => void) {
  _restoringListeners.add(cb);
  return () => { _restoringListeners.delete(cb); };
}

function getRestoringSnapshot() {
  return _isRestoring;
}

/** Reset para testes — não usar em produção. */
export function __resetRestoringState() {
  _isRestoring = true;
  _restoringListeners.clear();
  __resetOfflineDebugState();
}

/**
 * Resolve o userId do usuário atual, com fallback seguro para bootstrap offline.
 *
 * Prioridade:
 *   1. `user.id` (AppUser vindo de fetchMe — requer rede)
 *   2. Fallback localStorage, gateado por duas condições:
 *      a) Auth0 SDK indica sessão válida ou ainda carregando
 *      b) auth0User.sub === jurisapp:userSub (confirma mesmo usuário)
 *
 * O gate (b) garante que, se user B logasse no dispositivo de user A
 * com backend indisponível, o fallback seria rejeitado (subs diferentes).
 */
function resolveUserId(
  appUser: { id: string } | null,
  auth0Authenticated: boolean,
  auth0Loading: boolean,
  auth0Sub: string | undefined,
): string | null {
  if (appUser?.id) return appUser.id;

  const storedUserId = localStorage.getItem('jurisapp:userId');
  if (!storedUserId) return null;

  // Durante loading do Auth0 SDK, confiar no fallback (janela breve,
  // nenhum dado é exposto porque queries ficam bloqueadas por isRestoring)
  if (auth0Loading) return storedUserId;

  // Após Auth0 resolver: exigir sessão válida E sub matching
  if (auth0Authenticated && auth0Sub) {
    const storedSub = localStorage.getItem('jurisapp:userSub');
    if (auth0Sub === storedSub) return storedUserId;
  }

  return null;
}

/**
 * Orquestra o ciclo de vida do storage offline per-user.
 *
 * - Inicializa scope (userId) com fallback do localStorage para bootstrap offline
 * - Restaura query cache do disco (per-user) antes de liberar queries
 * - Subscreve auto-save do cache quando online
 * - Inicia auto-sync do outbox
 * - Limpa tudo ao deslogar ou trocar de conta
 *
 * Fonte de verdade de sessão: useAuth0() direto (token cacheado em localStorage),
 * NÃO useAuth()/useAuthContext().isAuthenticated (depende de fetchMe/rede).
 */
export function UserScopedPersistence({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const {
    isAuthenticated: auth0Authenticated,
    isLoading: auth0Loading,
    user: auth0User,
  } = useAuth0();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null>(null);
  const restoreGenerationRef = useRef(0);
  // Sinaliza que o restore de disco terminou para o userId corrente. O gate de
  // render (isRestoring) só é liberado quando ISTO E a identidade verificada
  // forem verdadeiros — nunca antes (ver efeito do render gate abaixo).
  const restoreSettledRef = useRef(false);
  // Bump de estado para forçar reavaliação do render gate quando o restore
  // assíncrono termina (refs não disparam re-render).
  const [, bumpRestoreSettled] = useState(0);

  const isRestoring = useSyncExternalStore(
    subscribeRestoring,
    getRestoringSnapshot,
    getRestoringSnapshot,
  );

  const userId = resolveUserId(user, auth0Authenticated, auth0Loading, auth0User?.sub);

  // Identidade verificada: user.id de fetchMe OU sub-matching pós-loading.
  // Durante auth0Loading o userId vem de fallback não-verificado — o restore de
  // disco pode RODAR em background (acelera relogin offline do mesmo usuário),
  // mas o cache NUNCA é renderizado (isRestoring liberado) nem o outbox
  // sincronizado antes desta confirmação. Isso fecha o vazamento cross-conta:
  // se user B abre o app no dispositivo de user A, o cache de A fica bloqueado
  // até o Auth0 resolver — e então o sub não confere e o scope é limpo.
  const identityVerified = !!user?.id || (
    !auth0Loading && auth0Authenticated && !!auth0User?.sub
    && auth0User.sub === localStorage.getItem('jurisapp:userSub')
  );

  // Estabilizar referência para o effect não re-rodar
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  // --- Efeito principal: inicializar/migrar scope quando userId muda ---
  useEffect(() => {
    const qc = queryClientRef.current;
    const prevUserId = prevUserIdRef.current;

    // Caso 1: sem userId (não autenticado ou sessão inválida)
    if (!userId) {
      if (prevUserId) {
        qc.clear();
        setCurrentUserId(null);
      }
      prevUserIdRef.current = null;
      restoreSettledRef.current = false;
      updateOfflineDebugState({
        currentUserId: null,
        lastRestoreError: null,
        lastRestoreFinishedAt: Date.now(),
        lastRestoreStartedAt: null,
        lastRestoreStatus: 'idle',
        lastRestoreUserId: null,
      }, 'restore-idle');
      setRestoring(false);
      return;
    }

    // Caso 2: mesmo userId, já restaurado — nada a fazer
    if (userId === prevUserId) {
      return;
    }

    // Caso 3: userId mudou (primeiro login, relogin, ou troca de conta)
    if (prevUserId) {
      qc.clear();
    }
    let unsubscribePersist: (() => void) | undefined;
    let cancelled = false;
    let finished = false;
    const restoreGeneration = ++restoreGenerationRef.current;

    // 1. Setar scope + religar gate de restore
    restoreSettledRef.current = false;
    setCurrentUserId(userId);
    updateOfflineDebugState({
      currentUserId: userId,
      lastRestoreError: null,
      lastRestoreFinishedAt: null,
      lastRestoreStartedAt: Date.now(),
      lastRestoreStatus: 'restoring',
      lastRestoreUserId: userId,
      persistBuster: PERSIST_BUSTER,
      restoreGeneration,
      restoreTimeoutMs: PERSIST_RESTORE_TIMEOUT_MS,
    }, 'restore-start');
    setRestoring(true);

    // 2. Purgar stores legados (one-shot, fire-and-forget)
    purgeLegacyStores();

    // 3. Criar persister e restaurar cache do disco
    const persister = createUserQueryPersister(userId);
    const finishRestore = (
      status: 'restored' | 'timeout' | 'error',
      error?: unknown,
    ) => {
      if (finished || cancelled || restoreGeneration !== restoreGenerationRef.current) return;

      finished = true;
      prevUserIdRef.current = userId;
      updateOfflineDebugState({
        currentUserId: userId,
        lastRestoreError: error instanceof Error ? error.message : error ? String(error) : null,
        lastRestoreFinishedAt: Date.now(),
        lastRestoreStatus: status,
        lastRestoreUserId: userId,
      }, `restore-${status}`);
      // Restore de disco concluído. O gate de render só é liberado pelo efeito
      // do render gate quando a identidade também estiver verificada.
      restoreSettledRef.current = true;
      bumpRestoreSettled((n) => n + 1);

      unsubscribePersist = persistQueryClientSubscribe({
        queryClient: qc,
        persister,
        buster: PERSIST_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: shouldPersistQuery,
        },
      });
    };
    const timeoutId = window.setTimeout(() => {
      finishRestore('timeout');
    }, PERSIST_RESTORE_TIMEOUT_MS);

    persistQueryClientRestore({
      queryClient: qc,
      persister,
      buster: PERSIST_BUSTER,
      maxAge: PERSIST_MAX_AGE,
    })
      .then(
        () => {
          finishRestore('restored');
        },
        (error) => {
          finishRestore('error', error);
        },
      )
      .catch(() => {}) // Falha de restore não bloqueia o app
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (cancelled || finished) return;
        prevUserIdRef.current = userId;
        restoreSettledRef.current = true;
        bumpRestoreSettled((n) => n + 1);

        // 4. Subscrever auto-save APÓS restore completo
        unsubscribePersist = persistQueryClientSubscribe({
          queryClient: qc,
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: shouldPersistQuery,
          },
        });
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      unsubscribePersist?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // --- Render gate: libera isRestoring só com restore concluído E identidade
  // verificada. Durante auth0Loading (identidade ainda não confirmada) o cache
  // permanece bloqueado mesmo que o restore de disco já tenha terminado —
  // impede que dados de leitura de outra conta sejam renderizados antes do
  // sub-match. O caso sem userId já libera o gate no efeito principal.
  useEffect(() => {
    if (!userId) return;
    if (restoreSettledRef.current && identityVerified) {
      setRestoring(false);
    }
  });

  // --- Efeito de auto-sync: só dispara quando identidade está verificada ---
  // Separado do efeito de restore porque o restore de leitura é seguro com
  // fallback não-verificado, mas o sync (escrita) exige identidade confirmada.
  useEffect(() => {
    if (!userId || !identityVerified) return;

    const cleanupSync = startAutoSync();
    return cleanupSync;
  }, [userId, identityVerified]);

  // --- AccessOracle: criado/destruído junto com o userId ---
  // useMemo recria o oracle quando userId muda; cleanup do useEffect chama
  // clear() (apaga IDB + memória) e desliga o singleton.
  const oracle = useMemo<AccessOracle | null>(() => {
    if (!userId) return null;
    return createAccessOracle({ queryClient, userId });
  }, [userId, queryClient]);

  useEffect(() => {
    setAccessOracle(oracle);
    return () => {
      // Cleanup ao trocar de userId — limpa storage do user anterior.
      // No logout (`oracle === null`), também garantimos o desligamento.
      if (oracle) void oracle.clear();
      setAccessOracle(null);
    };
  }, [oracle]);

  return (
    <IsRestoringProvider value={isRestoring}>
      <AccessOracleProvider value={oracle}>
        {children}
      </AccessOracleProvider>
    </IsRestoringProvider>
  );
}
